import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gauge, BadgeCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { QueueSideBySide } from './QueueSideBySide';

const STYLES = ['flat', 'hobbled', 'relaxed', 'cascade', 'aventura', 'pleated'];
const SEG = [['live', 'bg-green-600', 'Live'], ['awaiting_approval', 'bg-blue-400', 'QC pass awaiting approval'], ['manual_review', 'bg-amber-500', 'Manual review'], ['missing', 'bg-neutral-300', 'Missing']];

const Bar = ({ v, testId }) => (
  <div className="flex h-2 w-full overflow-hidden rounded-full bg-neutral-100" data-testid={testId}>
    {SEG.map(([k, cls]) => v[k] > 0 && <div key={k} className={cls} style={{ width: `${(100 * v[k]) / v.target}%` }} />)}
  </div>
);

/* Penthouse Living completion: every Roman colour × 6 styles must be live before Master Suite */
export const PenthouseProgress = ({ api, headers, refreshKey, onOpenQueue, onChanged }) => {
  const [d, setD] = useState(null);
  const load = useCallback(() => axios.get(`${api}/qc/penthouse-progress`, { headers }).then(({ data }) => setD(data)).catch(() => {}), [api, headers]);
  useEffect(() => { load(); }, [load, refreshKey]);
  const signOff = async () => {
    if (!window.confirm('Record the final sign-off for Penthouse Living? All 264 scenes are live.')) return;
    try { await axios.post(`${api}/qc/penthouse-signoff`, {}, { headers }); toast.success('Penthouse Living signed off'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Sign-off failed'); }
  };
  if (!d) return null;
  const t = d.total;
  const so = d.signoff;
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-3 space-y-2" data-testid="penthouse-progress">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <Gauge className="w-4 h-4 text-neutral-700" />
        <span className="font-medium text-neutral-900">Penthouse Living completion</span>
        <span className="text-neutral-600" data-testid="penthouse-progress-total">{t.live}/{t.target} live ({d.pct_live}%)</span>
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${so ? 'bg-green-100 text-green-800' : d.complete ? 'bg-blue-100 text-blue-800' : 'bg-neutral-100 text-neutral-600'}`} data-testid="penthouse-progress-status">
          {so ? `Signed off ${so.signed_at.slice(0, 10)} by ${so.attributed_to || so.signed_by}${so.attributed_to ? ' (attribution corrected)' : ''}` : d.complete ? '100% live — ready for sign-off' : 'Not signed off · Master Suite on hold'}
        </span>
        {!so && (
          <Button size="sm" className="h-7 text-[11px] gap-1 bg-green-700 hover:bg-green-800" disabled={!d.complete} onClick={signOff}
            title={d.complete ? '' : 'Available when every scene is live'} data-testid="penthouse-signoff-btn"><BadgeCheck className="w-3 h-3" />Sign off Penthouse Living</Button>
        )}
        <button className="ml-auto text-amber-700 font-medium hover:underline" onClick={onOpenQueue} data-testid="manual-review-queue-btn">
          Manual review queue ({t.manual_review})
        </button>
        <QueueSideBySide api={api} headers={headers} onChanged={() => { load(); onChanged?.(); }} />
      </div>
      <Bar v={t} testId="penthouse-progress-bar" />
      <div className="flex flex-wrap gap-3 text-[10px] text-neutral-600">
        {SEG.map(([k, cls, label]) => <span key={k} className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${cls}`} />{label}: {t[k]}</span>)}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
        {STYLES.map(s => {
          const v = d.styles[s];
          return (
            <div key={s} className="space-y-1 text-[10px]" data-testid={`penthouse-progress-${s}`}>
              <div className="flex justify-between"><span className="capitalize font-medium text-neutral-800">{s}</span><span className="text-neutral-500">{v.live}/{v.target}</span></div>
              <Bar v={v} testId={`penthouse-progress-bar-${s}`} />
              <p className="text-neutral-500">{v.awaiting_approval} awaiting · {v.manual_review} manual{v.missing ? ` · ${v.missing} missing` : ''}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};
